/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { FloatingPortal } from '@floating-ui/react'
import type { useFloating, useInteractions } from '@floating-ui/react'
import { Plus } from 'iconoir-react'
import CustomQuestion from 'app/components/scheduling/CustomQuestion'
import QuestionPanel from 'app/components/scheduling/QuestionPanel'
import type { CustomQuestionType } from 'app/types/entities'

/** The "Form Fields" editor shared verbatim by the scheduling settings and the
 * online-scheduler location pages: the read-only required-field preview plus the
 * draggable custom-questions list and its add-question floating panel. Extracted
 * so the identical block lives in one place. The prop types use the loosest
 * shape both call sites satisfy (both scheduling hooks return these fields). */
export type SchedulingFormFieldsCardProps = {
  customQuestions: CustomQuestionType[]
  showQuestionPanel: boolean
  setShowQuestionPanel: (v: boolean) => void
  locationId: string | undefined
  saveRequest: { inFlight: boolean }
  floatingData: ReturnType<typeof useFloating>
  getReferenceProps: ReturnType<typeof useInteractions>['getReferenceProps']
  getFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps']
  onMoveQuestion: (dragIndex: number, hoverIndex: number) => void
  onDropQuestion: () => Promise<void>
  onQuestionCreateCompleted: (question: CustomQuestionType) => Promise<void>
  onQuestionUpdateCompleted: (question: CustomQuestionType) => void
  onQuestionDeleted: (id: string) => void
}

export function SchedulingFormFieldsCard({
  customQuestions,
  showQuestionPanel,
  setShowQuestionPanel,
  locationId,
  saveRequest,
  floatingData,
  getReferenceProps,
  getFloatingProps,
  onMoveQuestion,
  onDropQuestion,
  onQuestionCreateCompleted,
  onQuestionUpdateCompleted,
  onQuestionDeleted,
}: SchedulingFormFieldsCardProps) {
  return (
    <>
      <div>
        <p className="text-heading-xs text-strong">Required</p>
        <div className="mt-2 grid grid-cols-2 gap-6 rounded-lg border border-default bg-surface p-4">
          <div className="col-span-1 flex flex-col gap-1.5">
            <p className="text-label-sm text-strong">First Name</p>
            <div className="h-10 rounded-lg border border-default bg-surface-subtle"></div>
          </div>
          <div className="col-span-1 flex flex-col gap-1.5">
            <p className="text-label-sm text-strong">Last Name</p>
            <div className="h-10 rounded-lg border border-default bg-surface-subtle"></div>
          </div>
          <div className="col-span-1 flex flex-col gap-1.5">
            <p className="text-label-sm text-strong">Email</p>
            <div className="h-10 rounded-lg border border-default bg-surface-subtle"></div>
          </div>
          <div className="col-span-1 flex flex-col gap-1.5">
            <p className="text-label-sm text-strong">Phone Number</p>
            <div className="h-10 rounded-lg border border-default bg-surface-subtle"></div>
          </div>
        </div>
      </div>
      <div>
        <p className="text-heading-xs text-strong">Custom Questions</p>
        <div className="mt-4 flex flex-col gap-y-4">
          {customQuestions.length ? (
            customQuestions?.map((question, i) => (
              <CustomQuestion
                key={question.id}
                locationId={locationId!}
                id={question.id}
                index={i}
                question={question}
                isMoveable={!saveRequest.inFlight}
                moveQuestion={onMoveQuestion}
                onDrop={onDropQuestion}
                onQuestionDeleted={(id) => onQuestionDeleted(id)}
                onQuestionUpdated={(question) => onQuestionUpdateCompleted(question)}
              />
            ))
          ) : (
            <p className="text-label-xs text-minimal">No custom questions</p>
          )}
          <button
            className="flex w-fit items-center gap-2 rounded-lg border border-default px-6 py-3"
            ref={floatingData.refs.setReference}
            {...getReferenceProps()}
          >
            <Plus height={16} width={16} className="text-strong" />
            <p className="text-label-sm text-strong">Add field</p>
          </button>
          {showQuestionPanel && (
            <FloatingPortal>
              <div
                className="rounded-lg border border-default bg-surface shadow-elevation-2"
                ref={floatingData.refs.setFloating}
                {...getFloatingProps()}
                style={{ ...floatingData.floatingStyles }}
              >
                <QuestionPanel
                  locationId={locationId!}
                  onSaved={onQuestionCreateCompleted}
                  onCancel={() => setShowQuestionPanel(false)}
                />
              </div>
            </FloatingPortal>
          )}
        </div>
      </div>
    </>
  )
}
